import { Injectable } from '@angular/core';
import { AppConfig } from '@iupics-config/app.config.service';
import * as CryptoJS from 'crypto-js';
import { CookieService } from 'ngx-cookie-service';
import shajs from 'sha.js';

export enum LocalStorageIupics {
  crypto_key = 'iu-lafka',
  default_language = 'iu-kawada',
  device_id = 'iu-Raruk',
}

@Injectable({
  providedIn: 'root',
})
export class IupicsCookieService {
  readonly cookieSeparator = '|=';

  // Cache for the decrypted+parsed default language ISO code (see getCachedDefaultLanguageIsoCode).
  // Avoids repeating the CryptoJS AES decrypt + JSON.parse on every call (e.g. every outgoing HTTP request).
  private isDefaultLanguageIsoCodeCached = false;
  private cachedDefaultLanguageIsoCode: string | undefined;

  constructor(
    private appConfig: AppConfig,
    private cookieService: CookieService
  ) {}

  generateRandomKeyCookie(): string {
    const randKey: string = shajs('sha256')
      .update(
        Math.random()
          .toString(36)
          .substring(2, 2 + 9) + Date.now()
      )
      .digest('hex');
    localStorage.setItem(LocalStorageIupics.crypto_key, randKey);
    return randKey;
  }

  setAndEncryptCookie(cookieName: string, value: string, expires?: Date) {
    const key = localStorage.getItem(LocalStorageIupics.crypto_key);
    const encodedCookie = CryptoJS.AES.encrypt(value, key);
    if (!expires) {
      expires = new Date();
      expires.setTime(expires.getTime() + this.appConfig.getConstant('cookieLifeTimeSecond') * 1000);
    }
    this.cookieService.set(cookieName, encodedCookie, expires, '/');
  }

  getDecryptedCookie(cookieName: string): string {
    const key = localStorage.getItem(LocalStorageIupics.crypto_key);
    const cookie = this.cookieService.get(cookieName);
    let decryptedCookie = '';
    try {
      decryptedCookie = CryptoJS.AES.decrypt(cookie, key).toString(CryptoJS.enc.Utf8);
    } catch (e) {
      this.clearLsCookies();
      location.reload();
    }
    return decryptedCookie;
  }

  setAndEncryptLocalStorage(keyName: string, value: string) {
    const key = localStorage.getItem(LocalStorageIupics.crypto_key);
    const encodedValue = CryptoJS.AES.encrypt(value, key);
    localStorage.setItem(keyName, encodedValue);
    if (keyName === LocalStorageIupics.default_language) {
      this.invalidateDefaultLanguageIsoCodeCache();
    }
  }

  getDecryptedLocalStorage(keyName: string): string {
    const key = localStorage.getItem(LocalStorageIupics.crypto_key);
    const encodedValue = localStorage.getItem(keyName);
    let decryptedValue = '';
    try {
      decryptedValue = CryptoJS.AES.decrypt(encodedValue, key).toString(CryptoJS.enc.Utf8);
    } catch (e) {
      this.clearLsCookies();
      location.reload();
    }
    return decryptedValue;
  }

  /**
   * Returns the ISO code of the default language stored (encrypted) in local storage,
   * decrypting and JSON-parsing it only once per value and caching the result, instead of
   * repeating that CPU-bound work on every call. The cache is invalidated whenever the
   * underlying local storage value is written, deleted or cleared.
   */
  getCachedDefaultLanguageIsoCode(): string | undefined {
    if (!this.isDefaultLanguageIsoCodeCached) {
      if (this.checkLS(LocalStorageIupics.default_language)) {
        try {
          this.cachedDefaultLanguageIsoCode = (
            JSON.parse(this.getDecryptedLocalStorage(LocalStorageIupics.default_language)) as { iso_code: string }
          ).iso_code;
        } catch (e) {
          this.cachedDefaultLanguageIsoCode = undefined;
        }
      } else {
        this.cachedDefaultLanguageIsoCode = undefined;
      }
      this.isDefaultLanguageIsoCodeCached = true;
    }
    return this.cachedDefaultLanguageIsoCode;
  }

  private invalidateDefaultLanguageIsoCodeCache(): void {
    this.isDefaultLanguageIsoCodeCached = false;
    this.cachedDefaultLanguageIsoCode = undefined;
  }

  check(cookieName: string): boolean {
    return this.cookieService.check(cookieName);
  }

  checkLS(keyName: string): boolean {
    return localStorage.getItem(keyName) !== null;
  }

  delete(cookieName: string) {
    this.cookieService.delete(cookieName);
  }
  clearLsCookies() {
    this.cookieService.deleteAll('/');
    localStorage.clear();
    this.invalidateDefaultLanguageIsoCodeCache();
  }

  deleteLS(keyName: string) {
    localStorage.removeItem(keyName);
    if (keyName === LocalStorageIupics.default_language) {
      this.invalidateDefaultLanguageIsoCodeCache();
    }
  }
}
